import React, { useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { useDispatch } from 'react-redux';
import { FloormapReduxCommandCreator } from 'appRedux/actions/floormapCommandCreator';
import { MapType } from 'common/common';
import {
  MAP_OVERLAY_ROOT_ATTR,
  useMapOverlayExpandCollapse,
} from 'hooks/useMapOverlayExpandCollapse';

type Props = {
  map: mapboxgl.Map;
  pinSelected: boolean;
  mapId: MapType;
};

const PlusIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20.1545 20.1544"
    fill="none"
    className="icon plus-icon"
    aria-hidden="true"
  >
    <path
      d="M8.81744 1.26011V8.81732H1.26023C0.564566 8.81732 0.000119306 9.38177 0.000119306 10.0774C0.000119306 10.7731 0.564566 11.3375 1.26023 11.3375H8.81744V18.8942C8.81744 19.5899 9.38189 20.1544 10.0776 20.1544C10.7732 20.1544 11.3377 19.5899 11.3377 18.8942V11.3375H18.8944C19.59 11.3375 20.1545 10.7731 20.1545 10.0774C20.1545 9.38177 19.59 8.81732 18.8944 8.81732H11.3377V1.26011C11.3377 0.564447 10.7732 0 10.0776 0C9.38189 0 8.81744 0.564447 8.81744 1.26011Z"
      fill="currentColor"
    />
  </svg>
);

const MinusIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20.1545 2.51923"
    fill="none"
    className="icon minus-icon"
    aria-hidden="true"
  >
    <path
      d="M1.25961 1.25961L18.8943 1.25961"
      stroke="currentColor"
      strokeWidth="2.51923"
      strokeLinecap="round"
    />
  </svg>
);

/** Pushpin from Figma map controls (Frame 6830) — already diagonal. */
const PinIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20.7273 20.7273"
    fill="none"
    className={className}
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.29869 1.80654C9.29869 0.619134 10.7343 0.0238009 11.574 0.863075L19.784 9.0731C20.6234 9.91284 20.028 11.3484 18.8406 11.3484H15.9986L13.0073 14.3382L13.4378 18.2208C13.5764 19.4726 12.062 20.201 11.1711 19.3106L7.06539 15.2049L1.23215 20.1613C1.1546 20.227 1.04918 20.2611 0.943748 20.257C0.838192 20.2524 0.740113 20.2099 0.668389 20.1381C0.596792 20.0663 0.555378 19.9684 0.551 19.8628C0.546925 19.7573 0.580932 19.6519 0.646651 19.5744L0.922009 19.2498L5.6031 13.7426L1.3365 9.47599C0.446002 8.58513 1.1745 7.07077 2.42633 7.20936L6.30888 7.63978L9.29869 4.64853V1.80654ZM11.5103 5.01374C11.5102 5.36699 11.37 5.70584 11.1204 5.95575L7.57408 9.50352C7.28813 9.78931 6.88751 9.93032 6.48569 9.88613L4.67268 9.68323L10.9639 15.9744L10.761 14.1614C10.7167 13.7595 10.8577 13.3591 11.1436 13.073L14.6899 9.52526C14.9398 9.27561 15.2801 9.13686 15.6334 9.13686H16.7189L11.5103 3.9268V5.01374Z"
      fill="currentColor"
    />
  </svg>
);

const MapControls: React.FC<Props> = ({ map, pinSelected }) => {
  const dispatch = useDispatch();
  const floormapCommand = FloormapReduxCommandCreator(dispatch);
  const [isPinned, setIsPinned] = useState(pinSelected);

  const { expanded, rootRef, expand, markInteraction, rootProps } = useMapOverlayExpandCollapse({
    map,
    collapsedAriaLabel: 'Expand map zoom controls',
    expandedAriaLabel: 'Map zoom controls',
  });

  const setMapInteractivity = (enable: boolean) => {
    const method = enable ? 'enable' : 'disable';
    map.boxZoom[method]();
    // Deliberately NOT toggling doubleClickZoom here: double-tapping a pin is reserved
    // for opening the EGM detail, so it stays disabled (set at map creation). Re-enabling
    // it on unpin let the floor zoom on double-tap and moved the pin out from under the
    // second tap, so the detail never opened.
    map.dragPan[method]();
    map.dragRotate[method]();
    map.keyboard[method]();
    map.scrollZoom[method]();
    map.touchZoomRotate[method]();
  };

  const handleZoom = (type: 'in' | 'out') => (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!expanded) {
      expand();
      return;
    }
    markInteraction();
    if (!isPinned) {
      type === 'in' ? map.zoomIn() : map.zoomOut();
    }
  };

  const handlePinToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!expanded) {
      expand();
      return;
    }
    markInteraction();
    const center = map.getCenter();
    floormapCommand.SetPinValues({
      bearing: map.getBearing(),
      latitudeLongitude: {
        longitude: center.lng,
        latitude: center.lat,
      },
      zoom: map.getZoom(),
      pinSelected: !isPinned,
    });

    setMapInteractivity(isPinned);
    setIsPinned(!isPinned);
  };

  useEffect(() => {
    setMapInteractivity(!pinSelected);
  }, [pinSelected]);

  useEffect(() => {
    setIsPinned(pinSelected);
  }, [pinSelected]);

  const className = [
    'map-controls',
    expanded ? 'map-controls--expanded' : 'map-controls--collapsed',
  ].join(' ');

  // While collapsed the root is role="button"; render spans (not nested buttons)
  // that still forward the first tap through handleZoom / handlePinToggle so the
  // panel expands — same tap-to-grow pattern as the heat legend sun/tiles.
  // Pin color/rotation still shows locked state.
  const zoomInControl = expanded ? (
    <button
      type="button"
      className="map-control-button zoom-in"
      onClick={handleZoom('in')}
      aria-label="Zoom in"
      disabled={isPinned}
    >
      <PlusIcon />
    </button>
  ) : (
    <span
      className="map-control-button zoom-in"
      aria-hidden="true"
      onClick={handleZoom('in')}
    >
      <PlusIcon />
    </span>
  );

  const zoomOutControl = expanded ? (
    <button
      type="button"
      className="map-control-button zoom-out"
      onClick={handleZoom('out')}
      aria-label="Zoom out"
      disabled={isPinned}
    >
      <MinusIcon />
    </button>
  ) : (
    <span
      className="map-control-button zoom-out"
      aria-hidden="true"
      onClick={handleZoom('out')}
    >
      <MinusIcon />
    </span>
  );

  const pinControl = expanded ? (
    <button
      type="button"
      className="map-control-button pin-toggle"
      onClick={handlePinToggle}
      aria-label={isPinned ? 'Unpin map' : 'Pin map'}
      aria-pressed={isPinned}
    >
      <PinIcon className={`icon pin-icon ${isPinned ? 'pinned' : 'unpinned'}`} />
    </button>
  ) : (
    <span
      className="map-control-button pin-toggle"
      aria-hidden="true"
      onClick={handlePinToggle}
    >
      <PinIcon className={`icon pin-icon ${isPinned ? 'pinned' : 'unpinned'}`} />
    </span>
  );

  return (
    <div className="map-control">
      <div
        ref={rootRef as React.RefObject<HTMLDivElement>}
        className={className}
        {...{ [MAP_OVERLAY_ROOT_ATTR]: '' }}
        {...rootProps}
      >
        {zoomInControl}
        <div className="divider" aria-hidden="true" />
        {zoomOutControl}
        <div className="divider" aria-hidden="true" />
        {pinControl}
      </div>
    </div>
  );
};

export default MapControls;
